import { useMemo } from 'react';

import SystemUsersRowStatus from '../users/SystemUsersRowStatus';

import type { User } from 'API';

import './_styles.scss';
import DateTimeOrNoData from 'components/content/DateTimeOrNoData';
import TextOrNoData from 'components/content/TextOrNoData';
import { DataTable, type TableColumn } from 'components/table';

interface UsersTeamTableProps {
    openDialogInfo: () => void;
    users: User[];
    selectedUserIds: string[];
    onToggleUser: (id: string) => void;
    isLoading: boolean;
}

const UsersTeamTable = ({ users, openDialogInfo, selectedUserIds, onToggleUser, isLoading }: UsersTeamTableProps) => {
    const columns = useMemo<TableColumn<User>[]>(
        () => [
            {
                key: 'firstName',
                title: 'First name',
                dataIndex: 'firstName',
            },
            {
                key: 'lastName',
                title: 'Last name',
                dataIndex: 'lastName',
            },
            {
                key: 'email',
                title: 'E-mail',
                dataIndex: 'email',
                width: 200,
            },
            {
                key: 'phone',
                title: 'Phone',
                dataIndex: 'phone',
                render: value => <TextOrNoData value={value} className='text-14' />,
            },
            {
                key: 'country',
                title: 'Country',
                dataIndex: 'country',
                render: value => <TextOrNoData value={value} className='text-14' />,
            },
            {
                key: 'city',
                title: 'City',
                dataIndex: 'city',
                render: value => <TextOrNoData value={value} className='text-14' />,
            },
            {
                key: 'roles',
                title: (
                    <>
                        Role
                        <span
                            className='rioglyph rioglyph-info-sign text-15 ml-5 cursor-pointer text-gray-dark'
                            onClick={openDialogInfo}
                        />
                    </>
                ),
                dataIndex: 'roles',
                render: (_, user) => (
                    <TextOrNoData value={user?.roles?.map(role => role.name).join(', ')} className='text-14' />
                ),
            },
            {
                key: 'status',
                title: 'Status',
                dataIndex: 'status',
                render: (_, user) => <SystemUsersRowStatus status={user.status} />,
            },
            {
                key: 'createdAt',
                title: 'Created',
                dataIndex: 'createdAt',
                width: 145,
                render: (_, user) => <DateTimeOrNoData value={user.createdAt} />,
            },
        ],
        [openDialogInfo]
    );

    const handleSelectionChanged = (selectedRowKeys: React.Key[]) => {
        const nextSelectedUserIds = selectedRowKeys.filter((key): key is string => typeof key === 'string');

        const changedUserIds = new Set([...selectedUserIds, ...nextSelectedUserIds]);

        changedUserIds.forEach(id => {
            if (selectedUserIds.includes(id) !== nextSelectedUserIds.includes(id)) {
                onToggleUser(id);
            }
        });
    };

    return (
        <DataTable
            stickyHeader
            isLoading={isLoading}
            data-testid='create-edit-teams-users-table'
            dataSource={users}
            columns={columns}
            maxHeight='350px'
            showSelectionColumn={true}
            onSelectionChange={handleSelectionChanged}
            selectedRowKeys={selectedUserIds}
            rowKey='id'
            empty={{
                title: 'No users yet, but they will appear here',
                body: 'You can go to the users tab and create a new user.',
            }}
        />
    );
};

export default UsersTeamTable;
